import { createSelector } from '@reduxjs/toolkit';

import { ProfileClass } from '../../library/profiles/ProfileClass';
import { RootState } from '../index';
import { getActiveMaterialSettings } from './getActiveMaterialSettings';
import { getComponentDesignInsertionInfoOrNull } from './insertionObjectSelectors';

export function getInsertingProfileTrussSettingsOrNull(state: RootState) {
  const componentDesignInsertionInfo = getComponentDesignInsertionInfoOrNull(state);
  if (!componentDesignInsertionInfo) return null;
  const { designSettings } = componentDesignInsertionInfo;
  return designSettings.definitionType === 'Profile' ? designSettings : null;
}

export function getInsertingProfileTrussSettings(state: RootState) {
  const insertingProfileTrussSettingsOrNull = getInsertingProfileTrussSettingsOrNull(state);
  if (!insertingProfileTrussSettingsOrNull) throw new Error('Expected to be inserting profile.');
  return insertingProfileTrussSettingsOrNull;
}

export function getInsertingProfileOrNull(state: RootState) {
  return getInsertingProfileTrussSettingsOrNull(state)?.profile ?? null;
}

export const getInsertingProfileClassOrNull = createSelector(
  getInsertingProfileTrussSettingsOrNull,
  (insertingProfileTrussSettingsOrNull) =>
    insertingProfileTrussSettingsOrNull ? ProfileClass.create(insertingProfileTrussSettingsOrNull) : null,
);

export function getInsertingProfileClass(state: RootState) {
  const insertingProfileClassOrNull = getInsertingProfileClassOrNull(state);
  if (!insertingProfileClassOrNull) throw new Error('Expected to be inserting profile.');
  return insertingProfileClassOrNull;
}

export function getInsertingProfile(state: RootState) {
  return getInsertingProfileTrussSettings(state).profile;
}

export function getInsertingOrRewebbingProfileOrNull(state: RootState) {
  if (state.application.rewebbingInfo) return state.application.rewebbingInfo.profileSettings.profile;
  return getInsertingProfileOrNull(state);
}

export function getInsertingOrRewebbingProfile(state: RootState) {
  if (state.application.rewebbingInfo) return state.application.rewebbingInfo.profileSettings.profile;
  return getInsertingProfile(state);
}

export function getTopChordWidthDuringProfileInsertion(state: RootState) {
  return getActiveMaterialSettings(state)?.profileMaterialSettings?.topChordLumber.actualWidth ?? 3.5;
}

export function getBottomChordWidthDuringProfileInsertion(state: RootState) {
  return getActiveMaterialSettings(state)?.profileMaterialSettings?.bottomChordLumber.actualWidth ?? 3.5;
}

export function getShouldRenderProfile(state: RootState) {
  return !!getInsertingProfileTrussSettingsOrNull(state) || !!state.application.rewebbingInfo;
}

export function isInsertingANonStructuralGable(state: RootState) {
  const profileSettings = getInsertingProfileTrussSettingsOrNull(state);
  if (!profileSettings) return false;
  return profileSettings.gableSettings.isGable && !profileSettings.gableSettings.isStructuralGable;
}

export function isInsertingAnAtticTruss(state: RootState) {
  const profileSettings = getInsertingProfileTrussSettingsOrNull(state);
  if (!profileSettings) return false;
  return profileSettings.profile.atticSettings !== null;
}
